import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { inject } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import type { GetFilter, GetPagination } from '@shared/interfacres/request-related';
import { DEFAULT_PAGE_SIZE } from '@shared/constants/basic';
import { Playlist } from '@shared/interfacres/playlist';
import { PlaylistsService } from '@data-access/user/services/playlists.service';
import { TrackApiData, TrackData } from '@shared/interfacres/track';
import { decreaseCounter, increaseCounter, markAsListened } from '@shared/helpers/tracks.helper';


interface PlaylistsState {
  list: Playlist[];
  tracks: TrackData[];
  currentPlaylistId: number;
  total: number;
  tracksTotal: number;
  isLoading: boolean;
  isTracksLoading: boolean;
  pagination: GetPagination;
  tracksPagination: GetPagination;
  tracksFiltering: GetFilter;
}

const initialState: PlaylistsState = {
  list: [],
  tracks: [],
  currentPlaylistId: 0,
  total: 0,
  isLoading: false,
  isTracksLoading: false,
  tracksTotal: 0,
  pagination: { page: 1, pageSize: 15 },
  tracksPagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
  tracksFiltering: { query: 'title', order: 'asc' }
};

export const PlaylistsStore = signalStore(
  withState(initialState),

  withMethods((store, service = inject(PlaylistsService)) => ({

    clearData() {
      patchState(store, () => ({
        list: [],
        tracks: [],
        currentPlaylistId: 0,
        total: 0,
        isLoading: false,
        isTracksLoading: false,
        tracksTotal: 0,
        pagination: { page: 1, pageSize: 15 },
        tracksPagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        tracksFiltering: { query: 'title', order: 'asc' } as { query: string, order: 'asc' | 'desc' }
      }));
    },
    clearPlaylistData() {
      patchState(store, () => ({
        tracks: [],
        currentPlaylistId: 0,
        isTracksLoading: false,
        tracksTotal: 0,
        tracksPagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        tracksFiltering: { query: 'title', order: 'asc' } as { query: string, order: 'asc' | 'desc' }
      }));
    },
    setPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        pagination: {
          ...state.pagination,
          page,
          pageSize,
        }
      }));
      return this.getFiltered();
    },

    async getFiltered() {
      patchState(store, () => ({
        isLoading: true,
      }));

      const withPagination = await firstValueFrom(service.getPlaylists(store.pagination()));
      patchState(store, () => ({
        isLoading: false,
        list: withPagination.data,
        total: withPagination.pagination.total
      }));
    },

    setCurrentPlaylist(playlistId: number) {
      patchState(store, () => ({
        currentPlaylistId: playlistId,
        tracksPagination: { page: 1, pageSize: DEFAULT_PAGE_SIZE },
        tracksFiltering: { query: 'title', order: 'asc' } as { query: string, order: 'asc' | 'desc' }
      }));
    },
    setPlaylistFilter(query: string, order: 'asc' | 'desc') {
      patchState(store, (state) => ({
        tracksFiltering: {
          ...state.tracksFiltering,
          query,
          order,
        }
      }));
    },
    setPlaylistPagination(page: number, pageSize: number) {
      patchState(store, (state) => ({
        tracksPagination: {
          ...state.tracksPagination,
          page,
          pageSize,
        }
      }));
    },
    async getPlaylistTracks() {
      patchState(store, () => ({
        isTracksLoading: true,
      }));

      const withPagination = await firstValueFrom(service.getTracks(store.currentPlaylistId(), store.tracksPagination(), store.tracksFiltering()));
      patchState(store, () => ({
        isTracksLoading: false,
        tracks: withPagination.data.map((track: TrackApiData) => {
          return {
            ...track,
            downloadsCount: track.download ? track.download[0]?.count : 0
            // updatedAt: genre.updated_at
          }
        }),
        tracksTotal: withPagination.pagination.total
      }));

    },
    increaseTrackCounter(id: number) {
      patchState(store, (state) => ({
        tracks: increaseCounter(id, state.tracks) as TrackData[]
      }))
    },
    decreaseTrackCounter(id: number) {
      patchState(store, (state) => ({
        tracks: decreaseCounter(id, state.tracks) as TrackData[]
      }))
    },
    markAsListened(id: number) {
      patchState(store, (state) => ({
        tracks: markAsListened(id, state.tracks) as TrackData[]
      }))
    },

  }))
);
